import { classifyFailure } from "@lafayette/api-client";
import type { StaffFulfilmentViewResponse, StaffRequestDetail } from "@lafayette/schemas";
import { useMemo, useState } from "react";
import { useParams } from "react-router";
import { AsyncState } from "../../ui/async-state";
import { Button } from "../../ui/button";
import { Card } from "../../ui/card";
import { Icon } from "../../ui/icon";
import { BackLink } from "../../ui/links";
import { Notice } from "../../ui/notice";
import { PageHeader } from "../../ui/page-header";
import { useRequestCapabilities, useRequestViewer } from "../access";
import {
  type RequestAction,
  type RequestViewer,
  reassignLabel,
  requestActions,
} from "../availability";
import type { AnyRequestCommand, RequestCommandOutcome } from "../commands";
import { useRequestCommands } from "../commands";
import {
  AskCustomerDialog,
  ClaimDialog,
  ClosureDialog,
  CompleteDialog,
  NoteDialog,
  PriorityDialog,
  ReassignDialog,
  StatusSummaryDialog,
  WorkStatusDialog,
} from "../components/CommandDialogs";
import {
  AttachmentDialog,
  ConfirmDialog,
  CorrectConfirmationDialog,
  PublishUpdateDialog,
  RecordEvidenceDialog,
  ShareAttachmentDialog,
  UnavailableDialog,
} from "../components/FulfilmentDialogs";
import {
  emptyNoteClass,
  hintClass,
  LookPill,
  PriorityBadge,
  StateBadge,
} from "../components/parts";
import { buildCustomerSees, readNotes, readRequestFacts, readTransitions } from "../detail-model";
import { useDialogSession } from "../dialog-session";
import { describeRequestError } from "../errors";
import {
  COVERAGE_GAP_DETAIL,
  COVERAGE_GAP_LABEL,
  flagLabels,
  flagLook,
  typeLabel,
} from "../labels";
import { useRequestDetail, useRequestFulfilment, useRequestQuestions } from "../queries";
import { useRequestsRuntime } from "../runtime";
import { CustomerSeesSection } from "../sections/CustomerSeesSection";
import {
  AttachmentsSection,
  CustomerUpdatesSection,
  EvidenceSection,
} from "../sections/FulfilmentSections";
import { PaymentQuoteSection } from "../sections/PaymentQuoteSection";
import {
  ContextSection,
  NotesSection,
  OwnershipSection,
  QuestionsSection,
  RequirementsSection,
  TimelineSection,
} from "../sections/StaffSections";

// The detail of one request (MD-R2) and every command on it. The page reads the request, then offers exactly the commands the state and
// the role allow (`availability.ts`). Each command is a dialog that sends the `expectedVersion` of the last read; a refusal (409) is
// shown in words and the page reads again. Staff-only data (notes, history, reasons, the work status) is marked with the staff-only
// words; "What the customer sees" is built from a closed model so the two cannot mix.

const DONE_NOTICE: Readonly<Record<string, string>> = {
  claim: "You accepted the request. It is now in progress.",
  reassign: "The request has a new holder.",
  askCustomer: "The customer has been asked. The request now waits for their answer.",
  workStatus: "The work status was changed.",
  unavailable: "The request was marked as unavailable.",
  decline: "The request was declined.",
  cancel: "The request was cancelled.",
  complete: "The request was completed.",
  priority: "The priority was changed.",
  statusSummary: "The line the customer sees was changed.",
  note: "The internal note was added.",
  confirm: "The request is confirmed on the evidence you chose.",
  recordEvidence: "The evidence was recorded. Only staff can see it.",
  publishUpdate: "The update was published. The customer can read it now.",
  correct:
    "The confirmation was corrected. The customer received an update marked as a correction.",
  attachment: "The file was uploaded. It is staff only until you share it.",
  shareAttachment: "The attachment is now shared with the customer.",
};

type BarAction = Exclude<
  RequestAction,
  "reassign" | "note" | "recordEvidence" | "publishUpdate" | "attachment" | "shareAttachment"
>;

const ACTION_LABELS: Readonly<Record<BarAction, string>> = {
  claim: "Accept and start",
  confirm: "Confirm with evidence",
  correct: "Correct the confirmation",
  ask: "Ask the customer",
  workStatus: "Set work status",
  complete: "Complete",
  unavailable: "Mark unavailable",
  decline: "Decline",
  cancel: "Cancel request",
  priority: "Change priority",
  statusSummary: "Edit the customer line",
};

const BAR_ORDER: readonly (BarAction | "reassign")[] = [
  "claim",
  "reassign",
  "ask",
  "workStatus",
  "confirm",
  "correct",
  "complete",
  "unavailable",
  "decline",
  "cancel",
  "priority",
  "statusSummary",
];

export function RequestDetailPage() {
  const { requestId = "" } = useParams();
  // The reads start from the roles of the session at once. The screen waits for `/staff/me` too, so no action shows and then turns off.
  const { capabilities: fromRoles } = useRequestCapabilities();
  const { capabilities, isLoading: rolesLoading } = useRequestViewer();
  const detail = useRequestDetail(requestId, fromRoles.readInternal);
  const questions = useRequestQuestions(requestId, fromRoles.readInternal);
  const fulfilment = useRequestFulfilment(requestId, fromRoles.readInternal);

  if (rolesLoading) {
    return (
      <AsyncState kind="loading" title="Checking your access" message="Reading your staff role." />
    );
  }
  if (!capabilities.readInternal) {
    return (
      <AsyncState
        kind="error"
        title="Not available for your role"
        message="Your staff role does not include the request detail."
      />
    );
  }
  if (detail.isPending) {
    return (
      <AsyncState kind="loading" title="Loading the request" message="Waiting for the answer." />
    );
  }
  if (detail.isError && detail.data === undefined) {
    const failure = describeRequestError(detail.error);
    return (
      <section className="grid gap-5">
        <BackLink to="/requests">Back to the queue</BackLink>
        <AsyncState
          kind="error"
          title={
            failure.kind === "forbidden"
              ? "You cannot open this request"
              : "The request did not load"
          }
          message={failure.message}
          {...(failure.kind === "forbidden" ? {} : { onRetry: () => void detail.refetch() })}
        />
      </section>
    );
  }
  return (
    <DetailBody
      key={requestId}
      detail={detail.data as StaffRequestDetail}
      questions={questions.data?.questions ?? []}
      questionsFailed={questions.isError}
      fulfilment={fulfilment.data ?? null}
      fulfilmentFailed={fulfilment.isError && fulfilment.data === undefined}
      refreshFailed={detail.isRefetchError}
      refreshing={detail.isFetching}
      onRefresh={() => {
        void detail.refetch();
        void questions.refetch();
        void fulfilment.refetch();
      }}
    />
  );
}

type BodyProps = Readonly<{
  detail: StaffRequestDetail;
  questions: NonNullable<ReturnType<typeof useRequestQuestions>["data"]>["questions"];
  questionsFailed: boolean;
  fulfilment: StaffFulfilmentViewResponse | null;
  fulfilmentFailed: boolean;
  refreshFailed: boolean;
  refreshing: boolean;
  onRefresh: () => void;
}>;

function DetailBody({
  detail,
  questions,
  questionsFailed,
  fulfilment,
  fulfilmentFailed,
  refreshFailed,
  refreshing,
  onRefresh,
}: BodyProps) {
  const { capabilities, staffUserId: viewerStaffUserId } = useRequestViewer();
  const runtime = useRequestsRuntime();
  const { run, release } = useRequestCommands();
  const dialog = useDialogSession<RequestAction>();
  const [notice, setNotice] = useState<string | null>(null);
  const [lostAccess, setLostAccess] = useState(false);
  // CHT-754: the download of one attachment. The signed link is opened at once and kept nowhere.
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [downloadMessage, setDownloadMessage] = useState<string | null>(null);

  const open = dialog.current?.action ?? null;
  const target = dialog.current?.target ?? null;
  const dialogId = dialog.current?.id ?? "";
  const facts = useMemo(() => readRequestFacts(detail), [detail]);
  const notes = useMemo(() => readNotes(detail), [detail]);
  const transitions = useMemo(() => readTransitions(detail), [detail]);
  const customerSees = useMemo(
    () => buildCustomerSees(facts, questions, fulfilment),
    [facts, questions, fulfilment],
  );

  const viewer: RequestViewer = { staffUserId: viewerStaffUserId, capabilities };
  // A closed request keeps the owner it had on the record, though the ownership view no longer names one.
  const owner =
    detail.ownership.owner ??
    (facts.ownerStaffUserId === null
      ? null
      : {
          staffUserId: facts.ownerStaffUserId,
          displayName: detail.actors[facts.ownerStaffUserId] ?? null,
        });
  const actions = requestActions(
    { state: facts.state, owner, assignee: detail.ownership.assignee },
    viewer,
  );
  const evidence = fulfilment?.evidence ?? [];
  const assigning = facts.state === "SUBMITTED" || detail.attention.exception === "COVERAGE_GAP";
  // Whoever holds the request now is not offered in the reassign list: the owner, or the designated assignee while it is ASSIGNED.
  const holderStaffUserId = owner?.staffUserId ?? detail.ownership.assignee?.staffUserId ?? null;

  // A close or a completion counts only while the dialog that made it is the open one (review F2). Closing drops the keys of the dialog
  // and reads the request again after a failure that may have run (review F3).
  function close(id: string) {
    if (!dialog.isCurrent(id)) return;
    dialog.close();
    release(id);
  }

  async function download(attachment: StaffFulfilmentViewResponse["attachments"][number]) {
    if (downloadingId !== null) return;
    setDownloadingId(attachment.id);
    setDownloadMessage(null);
    try {
      const link = await runtime.fulfilment.getAttachmentDownload(facts.id, attachment.id);
      runtime.openDownload(link.url);
    } catch (error) {
      // 403 and 404 are one answer on the server (a file that is gone looks like a file that is not yours). Plain words, no URL, no key.
      const { status } = classifyFailure(error);
      setDownloadMessage(
        status === 409
          ? // The stored file no longer matches what was attached (its size or type changed). It is not served.
            "This file changed after it was attached, so it cannot be opened. Do not use it. Ask a supervisor to check it."
          : status === 403 || status === 404
            ? "This file is not available. It may have been removed, or you may not have access to it."
            : "The file could not be opened. Try again in a moment.",
      );
    } finally {
      setDownloadingId(null);
    }
  }

  function done(id: string, outcome: RequestCommandOutcome) {
    if (!dialog.isCurrent(id)) return;
    if (open !== null) setNotice(DONE_NOTICE[open === "ask" ? "askCustomer" : open] ?? "Done.");
    dialog.close();
    release(id);
    if ("detail" in outcome && outcome.detail === null) setLostAccess(true);
  }

  if (lostAccess) {
    return (
      <section className="grid gap-5">
        <Notice tone="success" role="status">
          <p>{notice} You can no longer open this request, so the page returns to the queue.</p>
        </Notice>
        <div>
          <BackLink to="/requests">Back to the queue</BackLink>
        </div>
      </section>
    );
  }

  const common = {
    facts,
    run: (command: AnyRequestCommand) => run(command, dialogId),
    onDone: (outcome: RequestCommandOutcome) => done(dialogId, outcome),
    onClose: () => close(dialogId),
  } as const;

  return (
    <section className="grid gap-5" aria-busy={refreshing}>
      <div>
        <BackLink to="/requests">Back to the queue</BackLink>
      </div>
      <PageHeader
        eyebrow={
          <span className="font-bold tracking-[0.14em] text-link">
            {typeLabel(facts.requestType)} request
          </span>
        }
        title={facts.title}
        description={
          <span className="flex flex-wrap items-center gap-1.5 pt-1">
            <StateBadge state={facts.state} /> <PriorityBadge priority={facts.priority} />{" "}
            {detail.attention.flags.map((flag) => (
              <LookPill key={flag} look={flagLook[flag]}>
                {flagLabels[flag]}
              </LookPill>
            ))}
          </span>
        }
      />

      {notice === null ? null : (
        <Notice tone="success" role="status">
          <p>{notice}</p>
        </Notice>
      )}
      {refreshFailed ? (
        <Notice tone="warning" role="status">
          <p>
            The last reload failed. This page may be out of date. A command still checks the version
            on the server.
          </p>
          <Button variant="outline" size="sm" onClick={onRefresh}>
            <Icon name="refresh" />
            Reload now
          </Button>
        </Notice>
      ) : null}
      {detail.attention.exception === "COVERAGE_GAP" ? (
        <Notice tone="warning" role="alert">
          <p>
            <strong>{COVERAGE_GAP_LABEL}.</strong> {COVERAGE_GAP_DETAIL}
          </p>
        </Notice>
      ) : null}

      <Card>
        <fieldset className="m-0 flex min-w-0 flex-wrap items-start gap-3 border-0 p-4">
          <legend className="sr-only">Actions on this request</legend>
          {BAR_ORDER.map((action) => {
            const availability = actions[action];
            if (availability === null) return null;
            const label =
              action === "reassign" ? reassignLabel(facts.state, assigning) : ACTION_LABELS[action];
            const reasonId = `request-action-${action}-reason`;
            return (
              <div key={action} className="flex max-w-64 flex-col gap-1">
                <Button
                  variant={action === "claim" || action === "ask" ? "default" : "outline"}
                  disabled={!availability.available}
                  aria-describedby={availability.available ? undefined : reasonId}
                  onClick={() => {
                    setNotice(null);
                    dialog.open(action);
                  }}
                >
                  {label}
                </Button>
                {availability.available ? null : (
                  <span id={reasonId} className={hintClass}>
                    {availability.reason}
                  </span>
                )}
              </div>
            );
          })}
          {BAR_ORDER.every((action) => actions[action] === null) ? (
            <p className={emptyNoteClass}>No action is possible: the request has ended.</p>
          ) : null}
        </fieldset>
      </Card>

      <div className="grid items-start gap-5 xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <div className="grid min-w-0 gap-5">
          <CustomerSeesSection model={customerSees} />
          <RequirementsSection facts={facts} />
          <PaymentQuoteSection
            requestId={facts.id}
            requestState={facts.state}
            requestVersion={facts.version}
            // The owner or a supervisor quotes. When the browser does not know who the viewer is, the server decides.
            mayQuote={
              capabilities.reassignOwner ||
              viewerStaffUserId === null ||
              viewerStaffUserId === owner?.staffUserId
            }
            onChanged={onRefresh}
          />
          {fulfilment === null ? (
            fulfilmentFailed ? (
              <Notice tone="warning" role="status">
                <p>
                  The evidence, updates and attachments did not load. Reload the page to read them.
                </p>
              </Notice>
            ) : null
          ) : (
            <>
              <CustomerUpdatesSection
                updates={fulfilment.customerUpdates}
                truncated={fulfilment.customerUpdatesTruncated}
                actors={detail.actors}
                publishAction={actions.publishUpdate}
                onPublish={() => {
                  setNotice(null);
                  dialog.open("publishUpdate");
                }}
              />
              <EvidenceSection
                evidence={fulfilment.evidence}
                truncated={fulfilment.evidenceTruncated}
                actors={detail.actors}
                zone={facts.timeZone}
                recordAction={actions.recordEvidence}
                onRecord={() => {
                  setNotice(null);
                  dialog.open("recordEvidence");
                }}
                // A single row is corrected with a CORRECTION row, while the request is not CONFIRMED (a confirmation is corrected with
                // its own command, which tells the customer). A row that is already corrected cannot be corrected again.
                canCorrectRow={(row) =>
                  actions.recordEvidence?.available === true &&
                  facts.state !== "CONFIRMED" &&
                  row.supersededByEvidenceId === null
                }
                onCorrect={(row) => {
                  setNotice(null);
                  dialog.open("recordEvidence", row.id);
                }}
              />
              <AttachmentsSection
                attachments={fulfilment.attachments}
                truncated={fulfilment.attachmentsTruncated}
                actors={detail.actors}
                addAction={actions.attachment}
                shareAction={actions.shareAttachment}
                onAdd={() => {
                  setNotice(null);
                  dialog.open("attachment");
                }}
                onShare={(attachment) => {
                  setNotice(null);
                  dialog.open("shareAttachment", attachment.id);
                }}
                onDownload={(attachment) => void download(attachment)}
                downloadingId={downloadingId}
                downloadMessage={downloadMessage}
              />
            </>
          )}
          {questionsFailed ? (
            <Notice tone="warning" role="status">
              <p>The questions did not load. Reload the page to read them.</p>
            </Notice>
          ) : (
            <QuestionsSection questions={questions} actors={detail.actors} />
          )}
          <TimelineSection transitions={transitions} actors={detail.actors} />
        </div>
        <div className="grid min-w-0 gap-5">
          <ContextSection detail={detail} />
          <OwnershipSection detail={detail} facts={facts} viewerStaffUserId={viewerStaffUserId} />
          <NotesSection
            notes={notes}
            actors={detail.actors}
            action={
              actions.note?.available ? (
                <Button
                  variant="outline"
                  onClick={() => {
                    setNotice(null);
                    dialog.open("note");
                  }}
                >
                  Add an internal note
                </Button>
              ) : null
            }
          />
        </div>
      </div>

      {open === "claim" ? <ClaimDialog {...common} /> : null}
      {open === "reassign" ? (
        <ReassignDialog
          {...common}
          holderStaffUserId={holderStaffUserId}
          viewerStaffUserId={viewerStaffUserId}
          assigning={assigning}
        />
      ) : null}
      {open === "ask" ? <AskCustomerDialog {...common} /> : null}
      {open === "workStatus" ? <WorkStatusDialog {...common} /> : null}
      {open === "complete" ? <CompleteDialog {...common} /> : null}
      {open === "unavailable" ? <UnavailableDialog {...common} /> : null}
      {open === "decline" || open === "cancel" ? <ClosureDialog kind={open} {...common} /> : null}
      {open === "confirm" ? (
        <ConfirmDialog {...common} evidence={evidence} zone={facts.timeZone} />
      ) : null}
      {open === "correct" ? <CorrectConfirmationDialog {...common} /> : null}
      {open === "recordEvidence" ? (
        <RecordEvidenceDialog
          {...common}
          correctionOf={evidence.find((row) => row.id === target)}
        />
      ) : null}
      {open === "publishUpdate" ? <PublishUpdateDialog {...common} evidence={evidence} /> : null}
      {open === "attachment" ? <AttachmentDialog {...common} /> : null}
      {open === "shareAttachment" && fulfilment !== null ? (
        <ShareAttachmentDialog
          {...common}
          attachment={
            fulfilment.attachments.find((attachment) => attachment.id === target) ??
            fulfilment.attachments[0] ?? {
              id: "",
              displayName: "",
              storagePath: "",
              contentType: "application/pdf",
              sizeBytes: 1,
              visibility: "STAFF_ONLY",
              sharedAt: null,
              sharedByStaffUserId: null,
              createdByStaffUserId: "",
              createdAt: "",
            }
          }
        />
      ) : null}
      {open === "priority" ? <PriorityDialog {...common} /> : null}
      {open === "statusSummary" ? <StatusSummaryDialog {...common} /> : null}
      {open === "note" ? <NoteDialog {...common} /> : null}
    </section>
  );
}
